import {ClipboardModule} from '@angular/cdk/clipboard';
import {CommonModule} from '@angular/common';
import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {Router, RouterModule} from '@angular/router';
import {BlockUI} from 'primeng/blockui';
import {Breadcrumb} from 'primeng/breadcrumb';
import {ButtonModule} from 'primeng/button';
import {ConfirmDialog} from 'primeng/confirmdialog';
import {Popover} from 'primeng/popover';
import {Toast} from 'primeng/toast';
import {Toolbar} from 'primeng/toolbar';
import {Subscription} from 'rxjs';
import {CustomerDataService} from '../../../content/services/customer-data.service';
import {PaymentDataService} from '../../../content/services/payment-data.service';
import {SERVICE_ENDPOINTS} from '../../../core/configs/service-endpoints';
import {ErrorCode} from '../../../core/enums/error-code';
import {ZbvLoginStatus} from '../../../core/enums/zbv-login-status';
import {TopbarMenuOverlay} from '../../../core/interfaces/topbar-menu-overlay';
import {AuthService} from '../../../core/services/auth.service';
import {ErrorProviderService} from '../../../core/services/error-provider.service';
import {I18nService} from '../../../core/services/i18n.service';
import {NotificationService} from '../../../core/services/notification.service';
import {UserService} from '../../../core/services/user.service';
import {VersionService} from '../../../core/services/version.service';
import {SpsProfileDialogComponent} from '../../../custom/components/sps-profile-dialog/sps-profile-dialog.component';
import {SpsProgressDialogComponent} from '../../../custom/components/sps-progress-dialog/sps-progress-dialog.component';
import {MobileDetectionDirective} from '../../directives/mobile-detection.directive';
import {SpsMenuItem} from '../../interfaces/sps-menu-item';
import {LayoutService} from '../../services/layout.service';
import {SidebarMenuService} from '../../services/sidebar-menu.service';
import {GcMenuComponent} from '../gc-menu/gc-menu.component';
import {NotificationsOverlayMenuComponent} from '../notifications-overlay-menu/notifications-overlay-menu.component';
import {ProfileOverlayMenuComponent} from '../profile-overlay-menu/profile-overlay-menu.component';
import {TopbarMenuItemComponent} from '../topbar-menu-item/topbar-menu-item.component';
import {RuntimeConfigService} from '../../../core/services/runtime-config.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    GcMenuComponent,
    Toolbar,
    TopbarMenuItemComponent,
    ConfirmDialog,
    Toast,
    NotificationsOverlayMenuComponent,
    Popover,
    ProfileOverlayMenuComponent,
    SpsProfileDialogComponent,
    MobileDetectionDirective,
    BlockUI,
    Breadcrumb,
    ClipboardModule,
    SpsProgressDialogComponent
  ],
  templateUrl: './layout.component.html'
  /** DEPRECATED
   animations: [
   trigger('sidebarExpand', [
   state('collapsed', style({width: '70px'})),
   state('expanded', style({width: '240px'})),
   transition('collapsed <=> expanded', animate('300ms ease'))
   ]),
   trigger('shiftContent', [
   state('collapsed', style({marginLeft: '70px'})),
   state('expanded', style({marginLeft: '240px'})),
   transition('collapsed <=> expanded', animate('300ms ease'))
   ]),
   trigger('labelFade', [
   state('hidden', style({opacity: 0})),
   state('visible', style({opacity: 1})),
   transition('hidden <=> visible', animate('300ms ease'))
   ])
   ]
   */
})

export class LayoutComponent implements OnInit, AfterViewInit {

  /**
   * Initialization
   * Flags
   */
  loginOk: boolean = false;
  loading: boolean = true;
  menuInitialized: boolean = false;

  /**
   * extrnal link URL's
   */
  imprintUrl: string;
  agbUrl: string;
  privacyUrl: string;

  /** Date */
  public today: Date = new Date();
  /** Version */
  public appVersion: string;

  /** Topbar Menu */
  topbarMenuOverlays: TopbarMenuOverlay[] = [];
  activeTopbarMenuOverlay: TopbarMenuOverlay = null;

  /** Profile Dialog */
  activeProfileId: string;
  showProfileDialog: boolean = false;

  @ViewChild('tbmenu') popoverMenu: Popover;

  constructor(public authService: AuthService,
              private errorProviderService: ErrorProviderService,
              private customerDataService: CustomerDataService,
              private userService: UserService,
              private paymentDataService: PaymentDataService,
              private router: Router,
              public i18n: I18nService,
              public notificationService: NotificationService,
              public versions: VersionService,
              public layoutService: LayoutService,
              protected sidebarMenuService: SidebarMenuService,
              private readonly runtimeConfig: RuntimeConfigService) {
    this.initTopbarMenuOverlays();
  }

  ngOnInit() {
    this.imprintUrl = this.runtimeConfig.spsImprintUrl;
    this.agbUrl = this.runtimeConfig.spsAGBUrl;
    this.privacyUrl = this.runtimeConfig.spsPrivacyUrl;
    this.appVersion = this.runtimeConfig.version;
    this.versions.initVersions();
    /**
     * ZBV Authentification
     */
    if (this.authService.zbvLoginStatus === ZbvLoginStatus.NONE) {
      const subscription: Subscription = this.authService.login.subscribe({
        next: loginOk => {
          subscription.unsubscribe();
          this.loginOk = loginOk;
          /**
           * if zbv-auth failed navigate to error-page with 'auth' route-param as error-type
           */
          if (!loginOk) {
            this.router.navigate(['/error/auth']);
          }
          /**
           * get ZBV-claims for user-data and roles
           */
          const claims = this.authService.getZbvClaims();
          if (claims) {

            // console.log('claims', claims);

            if (!claims?.roleNames || claims?.roleNames.length === 0) {
              /**
               * Redirect to the ZBV no-permission page
               */
              window.location.replace(this.runtimeConfig.zbvNoPermissionUrl);
              // this.router.navigate(['/error/permission']);
            } else {
              // console.log('Access-Token:');
              // console.log(this.authService.accessToken);

              /**
               * pass roles to Auth-Service for app-permission-checks
               */
              this.authService.userRoles = claims.roleNames;

              this.customerDataService.findById(SERVICE_ENDPOINTS.GET_ORGANISATIONS, claims.organisationId).subscribe({
                next: (result) => {
                  if (result.successful) {
                    this.authService.organisationData = result.dto;
                    this.userService.findAll(SERVICE_ENDPOINTS.GET_USER_CURRENT).subscribe({
                      next: (result) => {
                        if (result.successful) {
                          this.authService.initCurrentUserData(result.dto);
                          /**
                           * get user after auth was successful
                           * pre initialize data (like global PaymentMethods and organisation
                           * PaymentTypes - needed for sidebar-menu and dashboard)
                           */
                          this.preInitializeDataOnStart();
                        } else {
                          this.errorProviderService.errorCode = ErrorCode.USER_DATA;
                          this.errorProviderService.errorMessage = result.message;
                          this.errorProviderService.errorDetails = result.details;
                          this.router.navigate(['/error/api']);
                        }
                      },
                      error: err => {
                        this.errorProviderService.errorCode = ErrorCode.USER_DATA;
                        this.errorProviderService.errorMessage = err?.message;
                        this.errorProviderService.errorDetails = err?.details;
                        this.router.navigate(['/error/api']);
                      }
                    });
                  } else {
                    this.errorProviderService.errorCode = ErrorCode.ORGANISATION_DATA;
                    this.errorProviderService.errorMessage = result.message;
                    this.errorProviderService.errorDetails = result.details;
                    this.router.navigate(['/error/api']);
                  }
                },
                error: (err) => {
                  this.errorProviderService.errorCode = ErrorCode.ORGANISATION_DATA;
                  this.errorProviderService.errorMessage = err?.message;
                  this.errorProviderService.errorDetails = err?.details;
                  this.router.navigate(['/error/api']);
                }
              });
            }
          } else {
            this.router.navigate(['/error/auth']);
          }
        },
        error: error => {
          subscription.unsubscribe();
          console.log('ZBV-Authentification-Error...', error);
          this.router.navigate(['/error/auth'], {replaceUrl: true});
        }
      });
      this.authService.performZbvLogin();
    } else {
      if (this.authService.hasValidToken && this.authService.isLoggedIn) {
        this.preInitializeDataOnStart();
      }
    }

  }

  /**
   * navigate to home component (dashboard)
   */
  ngAfterViewInit(): void {
  }


  /**
   * Startup Data-initialization if ZBV-Auth ist OK
   * @private
   */
  private preInitializeDataOnStart() {
    /**
     * Get global payment methods config
     */
    this.paymentDataService.findAll(SERVICE_ENDPOINTS.GET_PAYMENT_METHODS_CONFIG).subscribe({
      next: result => {

        if (result.successful) {
          this.paymentDataService.paymentMethodConfig = result.payload;
          /**
           * set loginOK to true to render layout.html
           * Move behind -> payment-method-config fetched
           */
          this.loginOk = true;
          /**
           * get customer payment data
           */
          const subscription: Subscription = this.paymentDataService.initTenantPaymentData().subscribe({
            next: success => {
              subscription.unsubscribe();
              /**
               * Init sidebar menu and route to dashboard
               */
              this.sidebarMenuService.initSidebarMenu();
              this.menuInitialized = true;
              this.router.navigate(['/dashboard'], {replaceUrl: true});
              this.loading = false;
            },
            error: err => {
              subscription.unsubscribe();
              this.errorProviderService.errorCode = ErrorCode.CUSTOMER_PAYMENT_DATA;
              this.errorProviderService.errorMessage = result.message;
              this.errorProviderService.errorDetails = result.details;
              this.router.navigate(['/error/api']);
            }
          })
        } else {
          this.errorProviderService.errorCode = ErrorCode.PAYMENT_METHODS_CONFIG;
          this.errorProviderService.errorMessage = result.message;
          this.errorProviderService.errorDetails = result.details;
          this.router.navigate(['/error/api']);
        }

      }, error(err) {
        this.errorProviderService.errorCode = ErrorCode.PAYMENT_METHODS_CONFIG;
        this.errorProviderService.errorMessage = err?.message;
        this.errorProviderService.errorDetails = err?.details;
        this.router.navigate(['/error/api']);
      }
    })
  }

  toggleMenu(): void {
    this.layoutService.menuExpanded.set(!this.layoutService.menuExpanded());
  }

  get sidebarState(): 'collapsed' | 'expanded' {
    return this.layoutService.menuExpanded() ? 'expanded' : 'collapsed';
  }

  get labelState(): 'visible' | 'hidden' {
    return this.layoutService.menuExpanded() ? 'visible' : 'hidden';
  }

  /**
   * Popover Menu-Overlays for topbar menus
   * @private
   */
  private initTopbarMenuOverlays() {
    this.topbarMenuOverlays.push({
      type: 'NOTIFICATIONS',
      i8nAriaLabelKey: 'NOTIFICATIONS'
    });
    this.topbarMenuOverlays.push({
      type: 'PROFILE',
      i8nAriaLabelKey: 'PROFILE'
    });
  }

  /**
   * callback for click on topbar menu item
   * @param name
   * @param event
   */
  topbarMenuItemClick(name: string, event: any) {
    let open: boolean = true;
    if (this.activeTopbarMenuOverlay) {
      if (this.activeTopbarMenuOverlay?.type === name) {
        /** click on active topbar menu = active topbar menu is closing */
        this.popoverMenu.hide();
        open = false;
      } else {
        /** click on inactive topbar menu = active topbar menu is closing */
        this.popoverMenu.hide();
      }
    }
    if (open) {
      /** open new popover for topbar menu */
      setTimeout(() => {
        this.activeTopbarMenuOverlay = this.topbarMenuOverlays.find(item => item.type === name);
        this.popoverMenu.show(event);
      }, 250);
    }
  }

  /**
   * reset active overlay menu after closing
   */
  onTopbarOverlayMenuHide() {
    this.activeTopbarMenuOverlay = null;
  }

  /**
   * any settings menu item was clicked
   * react on specific menu-item id
   * @param menuItem
   */
  onProfileMenuItemClick(menuItem: SpsMenuItem) {
    /**
     * open dialog for app/user settings
     */
    this.activeProfileId = menuItem.id;
    this.popoverMenu.hide();
    this.showProfileDialog = true;
  }

  /**
   * route to contact component and sync the active sidebar menu
   */
  /*
  routeToContact() {
    if (this.sidebarMenuService?.activeChildMenu) {
      this.sidebarMenuService.activeChildMenu.active = false;
    }
    if (this.sidebarMenuService?.activeParentMenu) {
      this.sidebarMenuService.activeParentMenu.active = false;
    }
    setTimeout(() => {
      this.sidebarMenuService.activeChildMenu = this.sidebarMenuService.contactChildMenu;
      this.sidebarMenuService.activeChildMenu.active = true;
      this.router.navigate(['/contact']);
    }, 10);
  }

   */

}
